/**
 * The offline identity (spec 15.2, 15.3). One online login writes a `sesi_lokal` row
 * (user, role, parish, lingkungan or stasi, PIN hash, last verified) and that row, not the
 * Supabase session, is what the app trusts afterwards: weeks of PIN unlocks need no
 * network. The Supabase client (./supabase) stays in the main thread and hands the worker
 * an access token per sync through `tokenProvider`; the worker's `sync:session` events
 * carry the API's reason codes, and only `user_deactivated`, `device_revoked` and
 * `user_revoked` lead to a wipe (./wipe), after "Mengerti". A refresh failure or a 401
 * only sets `perluMasukKembali`; unsynced work is never destroyed by a login problem.
 * A forgotten PIN is the same online login for the locked account only ("Lupa PIN?"),
 * followed by a new PIN over that account's row: nothing else on the device changes.
 *
 * Akun & keamanan (spec 2026-09-30, 5 and 6): the API may refuse a sign-in for a step, a
 * new password (`harus_ganti_sandi`) or the second step (`perlu_verifikasi_dua_langkah`).
 * The sign-in then waits in memory (`masukTertunda`) on the shared-device gate: its
 * session stays stored but no token leaves and no scope moves until the step is done
 * (`lanjutkanMasuk`); dropped any other way, the session of the person the device served
 * comes back. The same codes mid-session set `langkahAkun`, which the guard turns into
 * the step's screen.
 *
 * A role or place an admin changes on the server (plan 5 follow-up): online at start, at the
 * PIN and from the sync loop's runs (at most every `PERIKSA_PERAN_MS`), `periksaPeran` reads
 * /sesi for the served person; another scope goes through the shared-device gate's clear and
 * snapshot, the session follows, and the download screen says why (`peranBerubah`).
 *
 * Device-level facts live in `sync_state`: the enrolled device id, the active user, the
 * names the header shows before the first snapshot, the document cache policy and the
 * first-install flag. `createSessionController` is platform-neutral (tests drive it with
 * a fake worker, a fake auth and a fake fetch); ./app.ts wires the app's one instance
 * over the tab's database client. Nothing here names a DOM global, so the node test
 * project can compile it.
 */
import {
  API_PATHS,
  CLIENT_VERSION_HEADER,
  CLIENT_VERSION_HEADER_LAMA,
  DEVICE_HEADER,
  DEVICE_HEADER_LAMA,
  type EnrolResponse,
  isKodeLangkah,
  type LangkahAkun,
  langkahDariKode,
  lebihDariMaks,
  PERAN,
  type SesiResponse,
  type SyncScope,
  uuidv7,
  WIPE_CODES,
} from '@satu/shared'
import {
  type AlasanPenghapusan,
  cacheNamaParoki,
  SESI_KOSONG,
  type Sesi,
  useSesi,
} from '../app/store/sesi'
import type { DbClient, TokenProvider } from '../db/client'
import type { SyncSessionEvent } from '../db/protocol'
import {
  bacaLingkupData,
  LINGKUP_DATA_KEY,
  lingkupDataDari,
  lingkupIdentik,
} from '../sync/lingkupData'
import { tokenMilik } from '../sync/tokenPengguna'
import {
  kirimSebelumKeluar,
  type PemilikTertunda,
  periksaLingkupAktif,
  perubahanMilikLain,
  siapkanPerangkat,
} from './gantiPengguna'
import { langkahDariToken } from './langkahToken'
import {
  hashPin,
  IDLE_LOCK_MS,
  makeSalt,
  nextPinState,
  type PinLockState,
  pinLockedUntil,
  verifyPin,
} from './pin'
import { useUnduhan } from './unduhan'

export type SignInResult =
  | {
      ok: true
      accessToken: string
      userId: string
      /**
       * Whether the account has a verified second factor, as GoTrue's answer lists them; absent
       * when not known. With the token's own claims it tells the step an aal1 session will be
       * asked for (./langkahToken), so the sign-in opens it without a refused enrolment.
       */
      adaFaktor?: boolean
    }
  | { ok: false; alasan: 'tidak_cocok' | 'offline' }

/** What the session needs from Supabase Auth (./supabase); tests hand in a fake. */
export interface AuthProvider {
  signIn(email: string, password: string): Promise<SignInResult>
  /** The current access token, refreshed when expired; null when no session is stored. */
  accessToken(): Promise<string | null>
  /** Forget the stored session on this device (best effort on the server). */
  signOutLocal(): Promise<void>
  /**
   * Forget the stored session now, in this call, and revoke it at GoTrue without waiting:
   * unlike `signOutLocal`, nothing runs later that could remove or revoke a session another
   * sign-in stores meanwhile.
   */
  lupakanSesi(): void
  /** The stored session as an opaque value, or null: kept before a sign-in that may be refused. */
  storedSession(): string | null
  /** Sign the current session out (best effort on the server) and put `stored` back. */
  restoreSession(stored: string | null): Promise<void>
  /** Refresh the session now (the server changed its claims); the new access token, or null. */
  segarkan(): Promise<string | null>
  /**
   * The stored session's account e-mail as GoTrue has it now (`getUser`), or null: an e-mail
   * changed elsewhere is newer than the one this device stored at its last sign-in.
   */
  email?(): Promise<string | null>
}

/** `sync_state` keys this module owns (plus `kursor`, written by the sync loop). */
export const SESI_STATE_KEYS = {
  perangkat: 'perangkat_id',
  aktif: 'sesi_aktif',
  profil: 'profil_sesi',
  instalSelesai: 'instal_pertama_selesai',
  kursor: 'kursor',
} as const

interface ProfilTersimpan {
  pengguna_id: string
  nama_paroki: string | null
  nama_pendek: string | null
  nama_lingkungan: string | null
  /** A ketua stasi's stasi name; null for other roles, absent in a profile stored before it. */
  nama_stasi?: string | null
  /** The email of the last login, shown fixed on "Lupa PIN?" (absent before that screen existed). */
  email?: string
}

interface SesiLokalRow {
  pengguna_id: string
  nama: string
  peran: Sesi['peran']
  paroki_id: string | null
  lingkungan_id: string | null
  stasi_id: string | null
  pin_hash: string
  pin_garam: string
  pin_gagal: number
  pin_terkunci_sampai: string | null
}

export type SesiDb = Pick<
  DbClient,
  | 'ready'
  | 'request'
  | 'requestRaw'
  | 'setScope'
  | 'setTokenProvider'
  | 'onSessionError'
  | 'onSnapshotProgress'
  | 'syncNow'
>

export interface SessionDeps {
  db: SesiDb
  auth: AuthProvider
  fetch: (input: string, init?: RequestInit) => Promise<Response>
  apiBase: string
  clientVersion: string
  online(): boolean
  now(): number
  perangkat(): { nama: string; platform: string | null }
  layarSentuh(): boolean
  /** Destroys local data and returns to /masuk (./wipe in the app). */
  wipe(): Promise<void>
  /**
   * Something on screen is not saved yet (a form, a figure: the app's
   * `app/store/belumDisimpan`): the role check waits, as a step asked mid-session and the idle
   * lock do. None: never.
   */
  isianBelumDisimpan?(): boolean
}

/**
 * How often the sync loop's runs ask the server for the served person's role and place;
 * start and the PIN always ask (online), and a sign-in's own reading counts as one.
 */
export const PERIKSA_PERAN_MS = 15 * 60_000

/**
 * What `periksaPeran` did: `sama` the server agrees; `diperbarui` another role of the same
 * scope (the session follows, the data stays); `dibersihkan` another scope (the device was
 * cleared for a snapshot); `dilewati` nothing was changed (offline, a step pending, a form
 * unsaved, another person's work unsent, a wipe the server asked for, any failure).
 */
export type HasilPeriksaPeran = 'sama' | 'diperbarui' | 'dibersihkan' | 'dilewati'

/** Where a sign-in goes next; the last two are the account steps (spec 2026-09-30 akun, 5). */
export type LangkahMasuk = 'buat_pin' | 'unduhan' | 'beranda' | LangkahAkun

export type MasukHasil =
  | { ok: true; langkah: LangkahMasuk }
  /** `kedaluwarsa`: the sign-in that waited for a step is gone (left, or its session ended). */
  | { ok: false; alasan: 'tidak_cocok' | 'offline' | 'dihapus' | 'akun_lain' | 'kedaluwarsa' }
  /** Spec 15.4: another person's work on this device is unsent (./gantiPengguna). */
  | { ok: false; alasan: 'perubahan_lain'; pemilik: PemilikTertunda[] }

/**
 * A sign-in waiting for a step, as its screens hold it (`masukTertunda`): the same object while
 * that sign-in waits, a new one for the next. A screen names it in every account call; once
 * it is left, replaced or waited too long, the ticket opens nothing.
 */
export interface TiketMasuk {
  readonly pengguna: string
  readonly email: string
  readonly adaSandi: boolean
}

/**
 * Whom an account call is for: a waiting sign-in's ticket, or the id of the account served
 * (Akun, a step asked mid-session, the newcomer at Buat PIN) while no sign-in waits.
 */
export type UntukAkun = TiketMasuk | string

/** An account call of the sign-in steps: done, or refused with the API's code. */
export type HasilAkun<T extends object = object> =
  | ({ ok: true } & T)
  | { ok: false; kode: string; detail?: unknown }

export interface OpsiMasuk {
  /** "Buang N perubahan itu", confirmed: discard another person's unsent work first. */
  buangPerubahanLain?: boolean
}

export type PinHasil = { ok: true } | { ok: false; alasan: 'salah' | 'terkunci' }

export interface SessionController {
  /** After the database is ready: read `sesi_lokal`, set the sync scope and the token provider. */
  start(): Promise<void>
  /**
   * The online login. With `lupaPin` ("Lupa PIN?" on the PIN screen) only the locked
   * account is accepted: another account is refused before enrolment, its session signed
   * out and the locked account's put back; the same person always goes on to Buat PIN,
   * whose `simpanPin` replaces only their PIN hash and clears the counter and cooldown.
   * Every sign-in passes the shared-device gate (./gantiPengguna).
   */
  masuk(email: string, kataSandi: string, lupaPin?: boolean, opsi?: OpsiMasuk): Promise<MasukHasil>
  /** "Lupa PIN?": the email the locked account last signed in with here, or null. */
  emailTerakhir(): Promise<string | null>
  /**
   * The sign-in waiting for an account step (spec 2026-09-30 akun, 5), or null: none, or one
   * that waited longer than the idle lock (IDLE_LOCK_MS), which is then dropped.
   */
  masukTertunda(): TiketMasuk | null
  /**
   * When the sign-in this ticket names stops waiting (epoch ms: the start of its current step
   * plus IDLE_LOCK_MS), so its step screen ends with it; null when it no longer waits (left,
   * replaced, or waited too long, which drops it as `masukTertunda` does).
   */
  batasMasuk(tiket: TiketMasuk): number | null
  /**
   * Whose account the account calls and Supabase's MFA calls act on: the sign-in waiting for
   * a step, else the newcomer at Buat PIN, else the person the device serves ('' for none).
   * A stored session of anyone else (another tab of this browser, a waiting sign-in a reload
   * forgot) is never used for them.
   */
  penggunaAkun(): string
  /**
   * Whom Akun acts for: the person the device serves, only while no sign-in waits for a step and
   * no newcomer waits at Buat PIN; '' otherwise (Akun then acts for no one). Never the waiting
   * sign-in's account, which `penggunaAkun` names for the step screens.
   */
  penggunaDilayani(): string
  /**
   * After the step: enrol the device and read the profile again with the current token,
   * through the same shared-device gate as `masuk`. Anything but another step drops the
   * waiting sign-in (the person the device served gets their session back).
   */
  lanjutkanMasuk(): Promise<MasukHasil>
  /**
   * Leaves a waiting sign-in ("Masuk dengan akun lain"): the session of the person the device
   * served comes back; on a device that served the same person the waiting session is signed out.
   */
  batalkanMasuk(): Promise<void>
  /**
   * POST /akun/kata-sandi; the password typed at this sign-in stands for the current one.
   * GoTrue is expected to end every session of the account when the password changes: when
   * the session cannot be refreshed, the device signs in again with the new password.
   *
   * Every account call names whom its screen was opened for (`UntukAkun`): the waiting
   * sign-in's ticket, sent only while that very sign-in waits, or the account served, sent
   * only while no sign-in waits; always under a token of that account's own. Otherwise it
   * answers `kedaluwarsa` and sends nothing: a screen left open after its sign-in ended never
   * acts for the person the device serves, nor for a later sign-in of the same person.
   */
  gantiSandi(untuk: UntukAkun, baru: string, lama?: string): Promise<HasilAkun>
  kirimKodePemulihan(untuk: UntukAkun): Promise<HasilAkun<{ email: string; berlakuSampai: string }>>
  /**
   * A right code removes the factors on the server, which ends the account's sessions; with
   * the password of this sign-in the controller signs in again and continues (`masukLagi`).
   * A sign-in that goes through (a role that may go without the app: none since the owner's
   * ruling of 2026-10-02, when the server asks every role for it) leaves the offer of a new app
   * open, once (`tawaranPasang`).
   */
  periksaKodePemulihan(
    untuk: UntukAkun,
    kode: string,
  ): Promise<HasilAkun<{ masukLagi: MasukHasil | null }>>
  /** After the browser enrolled or removed a factor: the server logs it and forgets its cache. */
  catatDuaLangkah(untuk: UntukAkun): Promise<void>
  /**
   * Whether the offer of a new app after a recovery may open (/masuk/dua-langkah?tawaran=1):
   * only for the account that just recovered, only while the device is theirs (active, or at
   * its Buat PIN). A locked device, or the person it serves, never gets it from the address.
   */
  tawaranPasang(): boolean
  /** The offer's screen took it (or it is over): it does not open again. */
  lepasTawaranPasang(): void
  /**
   * A step's screen met a session GoTrue no longer knows (`session_not_found`: a factor
   * verified elsewhere ends the account's aal1 sessions). No code helps: the waiting sign-in
   * the ticket names is left (the served person's session comes back); for the account the
   * device serves, the device asks for a sign-in. Anyone else's ended session changes nothing.
   */
  sesiBerakhir(untuk: UntukAkun): Promise<void>
  /** Mid-session: the step the API asked for is done (or put off offline); sync again. */
  selesaikanLangkahAkun(): void
  /**
   * The served person's role and place as the server knows them now (/sesi), for an admin's
   * change made after the last password sign-in. Runs by itself online at start, at the PIN
   * and from the sync loop's runs; a call while one is running shares it. Never while offline,
   * while a sign-in is in progress or waits for a step, while a step is asked mid-session, a
   * wipe waits or a form holds unsaved changes, nor under anyone else's stored session.
   * Another scope runs the shared-device gate's clear (the person's own unsent ops stay) and
   * a snapshot; the session row, the profile names and the sync scope follow. A switched-off
   * or revoked account gets today's wipe path.
   */
  periksaPeran(): Promise<HasilPeriksaPeran>
  /** Akun: the name changed on the server; the header and the local session follow. */
  perbaruiNama(nama: string): Promise<void>
  /**
   * Akun: the account's e-mail as Supabase reports it, for "Lupa PIN?". `pengguna` is the
   * account the stored session that answered belongs to: only the served person's own e-mail,
   * read while no sign-in waits, reaches the device's profile.
   */
  perbaruiEmail(pengguna: string, email: string): Promise<void>
  /** First login on this device: hash the PIN, write `sesi_lokal`, activate. */
  simpanPin(pin: string): Promise<'unduhan' | 'beranda'>
  buka(pin: string): Promise<PinHasil>
  kunci(): Promise<void>
  ubahPin(pinLama: string, pinBaru: string): Promise<PinHasil>
  /** Sign out: the data stays, the session goes; the next open needs an online login. */
  keluar(): Promise<void>
  /**
   * Before Keluar: send this user's unsent work (one bounded sync, ./gantiPengguna) and
   * answer how much is still unsent.
   */
  kirimSebelumKeluar(batasMs?: number): Promise<number>
  /**
   * Pengaturan paroki changed the parish's short name (section 18): the header and the
   * stored profile follow at once on this device; other devices read it at their next login.
   */
  perbaruiNamaPendek(namaPendek: string | null): Promise<void>
  /**
   * The first download is done: the device is ready (the document cache choice that came
   * after it is retired, spec 2026-09-30, 9).
   */
  selesaikanInstal(): Promise<void>
  /** "Mengerti" on the wipe dialog. */
  konfirmasiPenghapusan(): Promise<void>
  /** Epoch ms until which the PIN refuses attempts, or null. */
  pinTerkunciSampai(): Promise<number | null>
  /** The sync loop has a cursor: the first snapshot completed. */
  sudahAdaKursor(): Promise<boolean>
  readonly tokenProvider: TokenProvider
}

type ApiResult =
  | { kind: 'ok'; body: unknown }
  | { kind: 'wipe'; reason: AlasanPenghapusan }
  | { kind: 'error'; status: number; code: string; detail?: unknown }
  | { kind: 'offline' }

/** Who the device served and which session was stored when a sign-in began (spec 15.4). */
interface Gerbang {
  sesiSebelum: string | null
  dilayani: string
}

/**
 * A sign-in the API refused for a step (spec 2026-09-30 akun, 5 and 6): whose password was
 * accepted, its e-mail, the password typed (it stands for the current one when the initial
 * password is replaced, and signs in again after a recovery), the Lupa PIN flag, the confirmed
 * discard, the gate it began under, and when it began waiting for its current step. In memory
 * only; a reload forgets it. One object per sign-in, updated in place: a continuation knows
 * its sign-in is still the one waiting by identity.
 */
interface Tertunda extends Gerbang {
  readonly pengguna: string
  readonly email: string
  kataSandi: string
  readonly lupaPin: boolean
  readonly buang: boolean
  /** `deps.now()` when it began waiting for its current step. */
  mulai: number
  /** What its screens hold and name in their account calls. */
  readonly tiket: TiketMasuk
}

/** A sign-in that stops at an account step and waits for it. */
function menungguLangkah(hasil: MasukHasil | null): boolean {
  if (hasil === null || !hasil.ok) return false
  return hasil.langkah === 'ganti_sandi' || hasil.langkah === 'dua_langkah'
}

function isWipeReason(code: string): code is AlasanPenghapusan {
  return WIPE_CODES.has(code as AlasanPenghapusan)
}

// Structural checks instead of the shared Zod schemas: the shell chunk stays free of Zod.
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string'
}

function isEnrolResponse(value: unknown): value is EnrolResponse {
  return isRecord(value) && value.ok === true && isRecord(value.perangkat)
}

function isSesiResponse(value: unknown): value is SesiResponse {
  if (!isRecord(value) || !isRecord(value.pengguna)) return false
  const p = value.pengguna
  if (typeof p.id !== 'string' || typeof p.nama !== 'string') return false
  if (!(PERAN as readonly string[]).includes(String(p.peran))) return false
  if (!isNullableString(p.paroki_id) || !isNullableString(p.lingkungan_id)) return false
  if (!isNullableString(p.dekenat_id)) return false
  const paroki = value.paroki
  if (
    paroki !== null &&
    !(
      isRecord(paroki) &&
      typeof paroki.id === 'string' &&
      typeof paroki.nama === 'string' &&
      isNullableString(paroki.nama_pendek)
    )
  ) {
    return false
  }
  const lingkungan = value.lingkungan
  if (
    lingkungan !== null &&
    !(
      isRecord(lingkungan) &&
      typeof lingkungan.id === 'string' &&
      typeof lingkungan.nama === 'string'
    )
  ) {
    return false
  }
  if (p.stasi_id !== undefined && !isNullableString(p.stasi_id)) return false
  const stasi = value.stasi
  if (
    stasi !== undefined &&
    stasi !== null &&
    !(isRecord(stasi) && typeof stasi.id === 'string' && typeof stasi.nama === 'string')
  ) {
    return false
  }
  return typeof value.server_time === 'string'
}

function scopeOf(sesi: Sesi): SyncScope {
  const scope: SyncScope = {
    device_id: sesi.deviceId,
    user_id: sesi.userId,
    peran: sesi.peran,
    paroki_id: sesi.parokiId,
    lingkungan_id: sesi.lingkunganId,
  }
  // Only a ketua stasi carries a stasi; every other scope keeps its five fields.
  if (sesi.stasiId !== null) scope.stasi_id = sesi.stasiId
  return scope
}

function sesiDariProfil(profil: SesiResponse, deviceId: string): Sesi {
  return {
    nama: profil.pengguna.nama,
    peran: profil.pengguna.peran,
    paroki:
      profil.paroki === null
        ? null
        : { nama: profil.paroki.nama, pendek: profil.paroki.nama_pendek ?? profil.paroki.nama },
    lingkungan: profil.lingkungan?.nama ?? null,
    userId: profil.pengguna.id,
    parokiId: profil.pengguna.paroki_id,
    lingkunganId: profil.pengguna.lingkungan_id,
    stasi: profil.stasi?.nama ?? null,
    stasiId: profil.pengguna.stasi_id ?? null,
    deviceId,
  }
}

function sesiDariBaris(row: SesiLokalRow, profil: ProfilTersimpan | null, deviceId: string): Sesi {
  const names = profil !== null && profil.pengguna_id === row.pengguna_id ? profil : null
  return {
    nama: row.nama,
    peran: row.peran,
    paroki:
      row.paroki_id === null || names?.nama_paroki == null
        ? null
        : { nama: names.nama_paroki, pendek: names.nama_pendek ?? names.nama_paroki },
    lingkungan: names?.nama_lingkungan ?? null,
    userId: row.pengguna_id,
    parokiId: row.paroki_id,
    lingkunganId: row.lingkungan_id,
    stasi: names?.nama_stasi ?? null,
    stasiId: row.stasi_id,
    deviceId,
  }
}

export function createSessionController(deps: SessionDeps): SessionController {
  const { db } = deps

  // --- database helpers (raw db:query: drizzle stays out of the shell chunk) -------------

  async function run(sql: string, params: unknown[]): Promise<void> {
    await db.request('db:query', { sql, params, method: 'run' })
  }

  async function getRow(sql: string, params: unknown[]): Promise<unknown[] | undefined> {
    const result = await db.request('db:query', { sql, params, method: 'get' })
    return result.rows as unknown[] | undefined
  }

  async function getState<T>(key: string): Promise<T | undefined> {
    const row = await getRow('SELECT nilai FROM sync_state WHERE kunci = ?', [key])
    if (row === undefined || row[0] === null || row[0] === undefined) return undefined
    return JSON.parse(String(row[0])) as T
  }

  async function setState(key: string, value: unknown): Promise<void> {
    await run(
      'INSERT INTO sync_state (kunci, nilai, diubah_pada) VALUES (?, ?, ?) ON CONFLICT(kunci) DO UPDATE SET nilai = excluded.nilai, diubah_pada = excluded.diubah_pada',
      [key, JSON.stringify(value ?? null), new Date(deps.now()).toISOString()],
    )
  }

  async function bacaSesiLokal(userId: string): Promise<SesiLokalRow | null> {
    const row = await getRow(
      'SELECT pengguna_id, nama, peran, paroki_id, lingkungan_id, pin_hash, pin_garam, pin_gagal, pin_terkunci_sampai, stasi_id FROM sesi_lokal WHERE pengguna_id = ?',
      [userId],
    )
    if (row === undefined) return null
    return {
      pengguna_id: String(row[0]),
      nama: String(row[1]),
      peran: String(row[2]) as Sesi['peran'],
      paroki_id: row[3] === null ? null : String(row[3]),
      lingkungan_id: row[4] === null ? null : String(row[4]),
      pin_hash: String(row[5]),
      pin_garam: String(row[6]),
      pin_gagal: Number(row[7] ?? 0),
      pin_terkunci_sampai: row[8] === null || row[8] === undefined ? null : String(row[8]),
      stasi_id: row[9] === null || row[9] === undefined ? null : String(row[9]),
    }
  }

  async function tulisKunciPin(userId: string, state: PinLockState): Promise<void> {
    await run(
      'UPDATE sesi_lokal SET pin_gagal = ?, pin_terkunci_sampai = ?, terakhir_aktif = ? WHERE pengguna_id = ?',
      [state.gagal, state.terkunciSampai, new Date(deps.now()).toISOString(), userId],
    )
  }

  // --- the API from the main thread (enrolment and the profile) --------------------------

  async function api(
    token: string,
    deviceId: string | null,
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
  ): Promise<ApiResult> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      [CLIENT_VERSION_HEADER]: deps.clientVersion,
      [CLIENT_VERSION_HEADER_LAMA]: deps.clientVersion,
      Accept: 'application/json',
    }
    if (deviceId !== null) {
      headers[DEVICE_HEADER] = deviceId
      headers[DEVICE_HEADER_LAMA] = deviceId
    }
    const init: RequestInit = { method, headers }
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    }
    let response: Response
    try {
      response = await deps.fetch(`${deps.apiBase}${path}`, init)
    } catch {
      return { kind: 'offline' }
    }
    let parsed: unknown = null
    try {
      parsed = await response.json()
    } catch {
      parsed = null
    }
    if (response.ok) return { kind: 'ok', body: parsed }
    const code =
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { error?: unknown }).error === 'string'
        ? (parsed as { error: string }).error
        : 'http_error'
    if (isWipeReason(code)) return { kind: 'wipe', reason: code }
    // A gateway page or a crash is the connection's; a server that names its failure (the
    // account calls' `surel_gagal`, `supabase_admin`) is told as that code. The sign-in
    // treats both alike.
    if (response.status >= 500 && code === 'http_error') return { kind: 'offline' }
    const detail = isRecord(parsed) ? parsed.detail : undefined
    return detail === undefined
      ? { kind: 'error', status: response.status, code }
      : { kind: 'error', status: response.status, code, detail }
  }

  async function enrol(token: string, deviceId: string): Promise<ApiResult> {
    const info = deps.perangkat()
    return api(token, null, 'POST', API_PATHS.enrol, {
      device_id: deviceId,
      nama: info.nama,
      platform: info.platform,
      versi_aplikasi: deps.clientVersion,
    })
  }

  // --- state transitions -------------------------------------------------------------

  function aktifkan(sesi: Sesi, instalSelesai: boolean): void {
    useSesi.setState({
      asli: sesi,
      status: 'aktif',
      instalSelesai,
      menunggu: null,
      perluMasukKembali: false,
      peranBerubah: false,
    })
    cacheNamaParoki(sesi)
  }

  // While a "Lupa PIN?" sign-in is checked, the stored session may be another account's:
  // the sync loop and the API client get no token until the locked one is certain again.
  let memeriksaAkun = false
  // Spec 15.4: every sign-in may hand the device to another person. Until the gate has
  // readied the device and `setScope` names the newcomer, no token leaves: the loop still
  // serves the previous person's outbox and cursor (./gantiPengguna).
  let gantiPengguna = false
  // Who the password of the sign-in in progress belonged to (null until it is accepted).
  let penggunaMasuk: string | null = null
  // The active user's scope check could not run at start (offline, another person's work
  // unsent): it runs at the next online unlock (./gantiPengguna `periksaLingkupAktif`).
  let lingkupDitunda = false
  // Akun & keamanan (spec 2026-09-30, 5 and 6): the sign-in the API refused for a step, held
  // until the step is done (`lanjutkanMasuk`) or the sign-in is dropped (`lepasTertunda`).
  let tertunda: Tertunda | null = null
  // The account that recovered its second step and may be offered a new app, once.
  let tawaran: string | null = null
  // When the server was last asked for the served person's role (`periksaPeran`; a sign-in's
  // own /sesi counts), and the check in flight.
  let terakhirPeriksaPeran: number | null = null
  let pemeriksaanPeran: Promise<HasilPeriksaPeran> | null = null

  /** A waiting sign-in ends after the idle lock's time on one step (a PIN would be asked). */
  const habis = (t: Tertunda) => deps.now() - t.mulai >= IDLE_LOCK_MS

  const tokenProvider: TokenProvider = async () => {
    // A sign-in that waited too long gives the served person's sync back (unless a step of
    // it is being continued right now, under the gate).
    if (tertunda !== null && habis(tertunda) && !gantiPengguna) await lepasTertunda()
    // A waiting sign-in's token belongs to no scope yet: no sync and no screen gets it.
    if (!deps.online() || memeriksaAkun || gantiPengguna || tertunda !== null) return null
    const token = await deps.auth.accessToken()
    if (token === null && useSesi.getState().status !== 'tanpa_sesi') {
      useSesi.setState({ perluMasukKembali: true })
    }
    // A run (or a screen's call) of the day asks for the role again, at most every 15 minutes;
    // the run goes on with this token meanwhile (the check stops the loop itself if it clears).
    if (
      token !== null &&
      (terakhirPeriksaPeran === null || deps.now() - terakhirPeriksaPeran >= PERIKSA_PERAN_MS)
    ) {
      void periksaPeran()
    }
    return token
  }

  function onSessionError(event: SyncSessionEvent): void {
    if (isWipeReason(event.reason)) useSesi.setState({ penghapusan: event.reason })
    else if (isKodeLangkah(event.reason)) {
      useSesi.setState({ langkahAkun: langkahDariKode(event.reason) })
    } else if (event.reason === 'unauthorized') useSesi.setState({ perluMasukKembali: true })
  }

  async function bacaInstalSelesai(): Promise<boolean> {
    return (await getState<boolean>(SESI_STATE_KEYS.instalSelesai)) === true
  }

  // --- the shared-device gate (spec 15.4) and the waiting sign-in (plan 5) ---------------

  /**
   * After another person's password was accepted, a sign-in that ends without reaching
   * `setScope` puts the session of the person the device served back (`restoreSession`).
   * The same person, or nobody's password accepted yet: nothing to put back.
   */
  async function kembalikanSesi(gerbang: Gerbang, pengguna: string | null): Promise<void> {
    if (pengguna === null || pengguna === gerbang.dilayani) return
    if (deps.auth.storedSession() === gerbang.sesiSebelum) return
    try {
      await deps.auth.restoreSession(gerbang.sesiSebelum)
    } catch {
      // The stored session is local; whatever the server answers, it is replaced.
    }
  }

  // The drop in progress: callers that meet it at once share it, so the session goes back once.
  let pelepasan: Promise<void> | null = null

  /**
   * Drops the sign-in waiting for a step without reaching `setScope` (left, replaced by a new
   * sign-in, the served person's PIN, a wipe, waited too long): another person's waiting
   * session never stays stored, and `tertunda` holds every token until the served person's
   * session is back.
   */
  function lepasTertunda(): Promise<void> {
    const t = tertunda
    if (t === null) return Promise.resolve()
    pelepasan ??= (async () => {
      try {
        await kembalikanSesi(t, t.pengguna)
      } finally {
        if (tertunda === t) tertunda = null
        pelepasan = null
      }
    })()
    return pelepasan
  }

  /** The waiting sign-in, unless it waited too long (then its drop begins). */
  function tertundaHidup(): Tertunda | null {
    if (tertunda === null || !habis(tertunda)) return tertunda
    void lepasTertunda()
    return null
  }

  function penggunaAkun(): string {
    const { menunggu, asli: sesi } = useSesi.getState()
    return tertundaHidup()?.pengguna ?? menunggu?.userId ?? sesi.userId
  }

  function penggunaDilayani(): string {
    const { menunggu, asli: sesi } = useSesi.getState()
    return tertundaHidup() === null && menunggu === null ? sesi.userId : ''
  }

  /**
   * Spec 15.4: no token leaves while a sign-in is checked (`gantiPengguna`), whichever way it
   * ends. A sign-in that goes through releases it itself just before `setScope`; one that
   * waits for an account step keeps every token held through `tertunda`. Any other end after
   * another person's password was accepted puts the session of the person the device served
   * back first, so the loop, still under that person's scope, never runs with the newcomer's
   * token. `masuk`, `lanjutkanMasuk` and a recovery's second sign-in all run under it.
   */
  async function diGerbang<T>(gerbang: Gerbang, jalan: () => Promise<T>): Promise<T> {
    gantiPengguna = true
    try {
      return await jalan()
    } finally {
      if (gantiPengguna && tertunda === null) await kembalikanSesi(gerbang, penggunaMasuk)
      gantiPengguna = false
    }
  }

  /**
   * The rest of a sign-in (`selesaikanMasuk`), after which it waits only for a step: one that
   * ends any other way without reaching `setScope` (offline, a wipe, a failure, a throw) is no
   * longer waiting, and the gate around it puts the served person's session back.
   */
  async function teruskanMasuk<T extends MasukHasil | null>(jalan: () => Promise<T>): Promise<T> {
    const t = tertunda
    let hasil: MasukHasil | null = null
    try {
      const h = await jalan()
      hasil = h
      return h
    } finally {
      // Only its own sign-in: one that replaced it meanwhile is not this continuation's.
      if (!menungguLangkah(hasil) && tertunda === t) tertunda = null
    }
  }

  /**
   * The sign-in after the password, and after any step the API asked for: enrol the device,
   * read the profile, ready the device and write the session. A step refusal keeps the
   * sign-in in `tertunda` and names the screen (spec 2026-09-30 akun, 6), with the step's own
   * time to wait. A session its own token shows will be refused for a step (aal1: every role
   * requires the second step) goes to that step without asking to enrol, as the refusal would
   * have sent it (uji C27: no 403 at every sign-in); `adaFaktor` is GoTrue's answer to the
   * password, null when not known. A sign-in dropped while this runs (left, replaced, waited
   * too long) never takes the device: each answer is checked against the very sign-in that
   * started it.
   */
  async function selesaikanMasuk(
    token: string,
    adaFaktor: boolean | null = null,
  ): Promise<MasukHasil> {
    const t = tertunda
    if (t === null) return { ok: false, alasan: 'kedaluwarsa' }
    const masihMenunggu = () => tertunda === t
    const langkah = (kode: Parameters<typeof langkahDariKode>[0]): MasukHasil => {
      t.mulai = deps.now()
      return { ok: true, langkah: langkahDariKode(kode) }
    }
    const dugaan = langkahDariToken(token, adaFaktor)
    if (dugaan !== null) return langkah(dugaan)
    const { email, lupaPin, buang } = t
    let deviceId = (await getState<string>(SESI_STATE_KEYS.perangkat)) ?? uuidv7(deps.now())
    let enrolled = await enrol(token, deviceId)
    if (enrolled.kind === 'error' && enrolled.code === 'device_mismatch') {
      // Spec 15.4: this browser was enrolled by another parish; until section 15 keys
      // the database by parish, the new user gets a device row of their own.
      deviceId = uuidv7(deps.now())
      enrolled = await enrol(token, deviceId)
    }
    if (!masihMenunggu()) return { ok: false, alasan: 'kedaluwarsa' }
    if (enrolled.kind === 'wipe') {
      tertunda = null
      useSesi.setState({ penghapusan: enrolled.reason })
      return { ok: false, alasan: 'dihapus' }
    }
    if (enrolled.kind === 'error' && isKodeLangkah(enrolled.code)) return langkah(enrolled.code)
    if (enrolled.kind !== 'ok') return { ok: false, alasan: 'offline' }
    if (!isEnrolResponse(enrolled.body)) return { ok: false, alasan: 'offline' }

    const profilResult = await api(token, deviceId, 'GET', API_PATHS.sesi)
    if (!masihMenunggu()) return { ok: false, alasan: 'kedaluwarsa' }
    if (profilResult.kind === 'wipe') {
      tertunda = null
      useSesi.setState({ penghapusan: profilResult.reason })
      return { ok: false, alasan: 'dihapus' }
    }
    if (profilResult.kind === 'error' && isKodeLangkah(profilResult.code)) {
      return langkah(profilResult.code)
    }
    if (profilResult.kind !== 'ok') return { ok: false, alasan: 'offline' }
    if (!isSesiResponse(profilResult.body)) return { ok: false, alasan: 'offline' }
    const profil = profilResult.body
    terakhirPeriksaPeran = deps.now()
    const sesi = sesiDariProfil(profil, deviceId)
    await siapkanPerangkat(
      db,
      {
        user_id: sesi.userId,
        peran: sesi.peran,
        paroki_id: sesi.parokiId,
        lingkungan_id: sesi.lingkunganId,
        dekenat_id: profil.pengguna.dekenat_id,
        stasi_id: sesi.stasiId,
      },
      buang,
    )
    if (!masihMenunggu()) return { ok: false, alasan: 'kedaluwarsa' }
    // The device now serves the newcomer: the previous person's lock no longer opens it,
    // nor does a reload before the newcomer's PIN (start() then finds no active user), nor
    // does a step the API asked of the previous person, nor Akun's note of their password.
    if (sesi.userId !== useSesi.getState().asli.userId) {
      useSesi.setState({
        asli: SESI_KOSONG,
        status: 'tanpa_sesi',
        langkahAkun: null,
        sandiDiganti: false,
      })
    } else {
      // The same person signed in again (a session GoTrue ended after a refusal mid-session):
      // the API took this session's token, so the step it asked of the old one is done.
      useSesi.setState({ langkahAkun: null, sandiDiganti: false })
    }
    const aktifSebelum = await getState<string | null>(SESI_STATE_KEYS.aktif)
    if (aktifSebelum != null && aktifSebelum !== sesi.userId) {
      await setState(SESI_STATE_KEYS.aktif, null)
    }

    await setState(SESI_STATE_KEYS.perangkat, deviceId)
    const baru: ProfilTersimpan = {
      pengguna_id: sesi.userId,
      nama_paroki: profil.paroki?.nama ?? null,
      nama_pendek: profil.paroki?.nama_pendek ?? null,
      nama_lingkungan: profil.lingkungan?.nama ?? null,
      nama_stasi: profil.stasi?.nama ?? null,
      email,
    }
    await setState(SESI_STATE_KEYS.profil, baru)
    if (!masihMenunggu()) return { ok: false, alasan: 'kedaluwarsa' }
    // The sync run that follows builds a fresh device from a snapshot while the PIN is typed.
    // The sign-in no longer waits either: from here the newcomer's token may leave.
    gantiPengguna = false
    tertunda = null
    await db.setScope(scopeOf(sesi))

    const instalSelesai = await bacaInstalSelesai()
    const existing = await bacaSesiLokal(sesi.userId)
    // A forgotten PIN keeps the row, its counter and its cooldown until the new PIN is saved.
    if (existing !== null && !lupaPin) {
      await run(
        'UPDATE sesi_lokal SET nama = ?, peran = ?, paroki_id = ?, lingkungan_id = ?, stasi_id = ?, pin_gagal = 0, pin_terkunci_sampai = NULL, terakhir_diverifikasi = ?, terakhir_aktif = ? WHERE pengguna_id = ?',
        [
          sesi.nama,
          sesi.peran,
          sesi.parokiId,
          sesi.lingkunganId,
          sesi.stasiId,
          new Date(deps.now()).toISOString(),
          new Date(deps.now()).toISOString(),
          sesi.userId,
        ],
      )
      await setState(SESI_STATE_KEYS.aktif, sesi.userId)
      aktifkan(sesi, instalSelesai)
      return { ok: true, langkah: instalSelesai ? 'beranda' : 'unduhan' }
    }
    useSesi.setState({ menunggu: sesi, perluMasukKembali: false })
    return { ok: true, langkah: 'buat_pin' }
  }

  // --- the account calls of the sign-in steps and of Akun (plan 5) -----------------------

  async function hasilGagal(r: ApiResult): Promise<{ ok: false; kode: string; detail?: unknown }> {
    if (r.kind === 'wipe') {
      useSesi.setState({ penghapusan: r.reason })
      // A wipe drops a waiting sign-in too; the served person's session comes back first.
      await lepasTertunda()
      return { ok: false, kode: r.reason }
    }
    if (r.kind === 'error') {
      return r.detail === undefined
        ? { ok: false, kode: r.code }
        : { ok: false, kode: r.code, detail: r.detail }
    }
    return { ok: false, kode: 'offline' }
  }

  /**
   * The current session's token for an account call, or null (the callers answer `offline`
   * or `kedaluwarsa`): only a token whose `sub` is `penggunaAkun()`. While a sign-in waits,
   * the waiting account's own (the token provider holds it from every sync and screen; these
   * calls are that account's steps); one that waited too long is dropped, and its calls are
   * never sent under the served person's token instead. The stored session is shared by every
   * tab of this browser: another tab's sign-in, or a waiting sign-in a reload forgot, never
   * acts for the person this tab serves.
   */
  /** Whom an account call is for, or null when its screen no longer stands for anyone. */
  function sasaranAkun(untuk: UntukAkun): { t: Tertunda | null; pengguna: string } | null {
    if (typeof untuk === 'string') {
      // By its id only while no sign-in waits: a waiting sign-in's screens name its ticket.
      if (untuk === '' || tertunda !== null || penggunaAkun() !== untuk) return null
      return { t: null, pengguna: untuk }
    }
    // The very sign-in the ticket was given for, still waiting: never a later one.
    const t = tertundaHidup()
    return t !== null && t.tiket === untuk ? { t, pengguna: t.pengguna } : null
  }

  async function tokenAkun(
    untuk: UntukAkun,
  ): Promise<{ token: string; t: Tertunda | null; pengguna: string } | null> {
    if (!deps.online()) return null
    if (tertunda !== null && habis(tertunda)) {
      await lepasTertunda()
      return null
    }
    const sasaran = sasaranAkun(untuk)
    if (sasaran === null) return null
    const token = await deps.auth.accessToken()
    if (token === null || !tokenMilik(token, sasaran.pengguna)) return null
    // Nothing began waiting, nor did the sign-in end, while the token was read.
    return tertunda === sasaran.t ? { token, ...sasaran } : null
  }

  /** The e-mail the account last signed in with on this device, for its own sign-in again. */
  async function emailAkun(pengguna: string): Promise<string | null> {
    const profil = await getState<ProfilTersimpan>(SESI_STATE_KEYS.profil)
    return profil?.pengguna_id === pengguna ? (profil.email ?? null) : null
  }

  // --- the served person's role and place on the server (plan 5 follow-up) ---------------

  /** A sign-in in progress, or one waiting for a step: the device may be about to change hands. */
  const adaMasuk = () => memeriksaAkun || gantiPengguna || tertunda !== null

  /**
   * Whether `userId`'s role may be asked for now: online, the device theirs (locked or open,
   * not at Buat PIN), no sign-in in progress or waiting for a step, no step asked mid-session,
   * no wipe waiting, no form with unsaved changes, and no active-user check of its own still
   * pending (`lingkupDitunda`: the PIN runs that one first, then this one, never both at once
   * over a session one of them has just changed).
   */
  function bolehPeriksaPeran(userId: string): boolean {
    const s = useSesi.getState()
    return (
      userId !== '' &&
      deps.online() &&
      !lingkupDitunda &&
      !adaMasuk() &&
      s.asli.userId === userId &&
      (s.status === 'aktif' || s.status === 'terkunci') &&
      s.menunggu === null &&
      s.langkahAkun === null &&
      s.penghapusan === null &&
      deps.isianBelumDisimpan?.() !== true
    )
  }

  function periksaPeran(): Promise<HasilPeriksaPeran> {
    if (pemeriksaanPeran !== null) return pemeriksaanPeran
    const { asli: sesi } = useSesi.getState()
    if (!bolehPeriksaPeran(sesi.userId)) return Promise.resolve('dilewati')
    terakhirPeriksaPeran = deps.now()
    const jalan: Promise<HasilPeriksaPeran> = bandingkanPeran(sesi)
      // Any failure changes nothing: the next start, unlock or run asks again.
      .catch((): HasilPeriksaPeran => 'dilewati')
      .finally(() => {
        if (pemeriksaanPeran === jalan) pemeriksaanPeran = null
      })
    pemeriksaanPeran = jalan
    return jalan
  }

  async function bandingkanPeran(sesi: Sesi): Promise<HasilPeriksaPeran> {
    const boleh = () => bolehPeriksaPeran(sesi.userId)
    const token = await deps.auth.accessToken()
    // Only under the served person's own session: another tab may have signed someone in.
    if (token === null || !tokenMilik(token, sesi.userId) || !boleh()) return 'dilewati'
    const r = await api(token, sesi.deviceId, 'GET', API_PATHS.sesi)
    if (!boleh()) return 'dilewati'
    if (r.kind === 'wipe') {
      // Switched off or revoked: the dialog, then the wipe, as a refused sync leads to.
      useSesi.setState({ penghapusan: r.reason })
      return 'dilewati'
    }
    if (r.kind === 'error' && isKodeLangkah(r.code)) {
      useSesi.setState({ langkahAkun: langkahDariKode(r.code) })
      return 'dilewati'
    }
    if (r.kind !== 'ok' || !isSesiResponse(r.body) || r.body.pengguna.id !== sesi.userId) {
      return 'dilewati'
    }
    const profil = r.body
    const baru = sesiDariProfil(profil, sesi.deviceId)
    const pengguna = {
      user_id: baru.userId,
      peran: baru.peran,
      paroki_id: baru.parokiId,
      lingkungan_id: baru.lingkunganId,
      dekenat_id: profil.pengguna.dekenat_id,
      stasi_id: baru.stasiId,
    }
    const sesiSama =
      baru.peran === sesi.peran &&
      baru.parokiId === sesi.parokiId &&
      baru.lingkunganId === sesi.lingkunganId &&
      baru.stasiId === sesi.stasiId
    // The scope the data was fetched under (a dekan's deanery lives only there). A device that
    // cannot tell is the start check's business while the session row agrees.
    const tercatat = bacaLingkupData(await getState<unknown>(LINGKUP_DATA_KEY))
    const lingkupSama =
      tercatat === null ? sesiSama : lingkupIdentik(tercatat, lingkupDataDari(pengguna))
    if (sesiSama && lingkupSama) return 'sama'
    let dibersihkan = false
    if (!lingkupSama) {
      if (!boleh()) return 'dilewati'
      try {
        // The gate of a change of signed-in user: nothing of another scope stays, the
        // person's own unsent ops do; another person's unsent work holds it back (it throws).
        dibersihkan = (await siapkanPerangkat(db, pengguna, false)).dibersihkan
      } catch {
        return 'dilewati'
      }
    }
    // A sign-in that began meanwhile readies and scopes the device itself; if it is left, the
    // next check (the PIN asks) finds the scope recorded and puts the session back.
    if (useSesi.getState().asli.userId !== sesi.userId || adaMasuk()) return 'dilewati'
    // The clear took the active-user pointer and the cursor with the data's keys (here or in a
    // check that stopped above): the pointer comes back, the download screen shows.
    if ((await getState<string | null>(SESI_STATE_KEYS.aktif)) !== sesi.userId) {
      await setState(SESI_STATE_KEYS.aktif, sesi.userId)
    }
    const unduhUlang = dibersihkan || (await getState<number>(SESI_STATE_KEYS.kursor)) === undefined
    await run(
      'UPDATE sesi_lokal SET nama = ?, peran = ?, paroki_id = ?, lingkungan_id = ?, stasi_id = ? WHERE pengguna_id = ?',
      [baru.nama, baru.peran, baru.parokiId, baru.lingkunganId, baru.stasiId, sesi.userId],
    )
    const tersimpan = await getState<ProfilTersimpan>(SESI_STATE_KEYS.profil)
    const profilBaru: ProfilTersimpan = {
      ...(tersimpan?.pengguna_id === sesi.userId ? tersimpan : {}),
      pengguna_id: sesi.userId,
      nama_paroki: profil.paroki?.nama ?? null,
      nama_pendek: profil.paroki?.nama_pendek ?? null,
      nama_lingkungan: profil.lingkungan?.nama ?? null,
      nama_stasi: profil.stasi?.nama ?? null,
    }
    await setState(SESI_STATE_KEYS.profil, profilBaru)
    // After a clear the loop has no scope: this one starts the snapshot.
    await db.setScope(scopeOf(baru))
    useSesi.setState(
      unduhUlang ? { asli: baru, instalSelesai: false, peranBerubah: true } : { asli: baru },
    )
    cacheNamaParoki(baru)
    return dibersihkan ? 'dibersihkan' : 'diperbarui'
  }

  const controller: SessionController = {
    tokenProvider,

    async start() {
      db.setTokenProvider(tokenProvider)
      db.onSessionError(onSessionError)
      db.onSnapshotProgress((progress) => useUnduhan.getState().terima(progress, deps.now()))
      try {
        await db.ready
      } catch {
        useSesi.setState({ status: 'tanpa_sesi' })
        return
      }
      const [aktif, deviceId, profil, instalSelesai] = await Promise.all([
        getState<string>(SESI_STATE_KEYS.aktif),
        getState<string>(SESI_STATE_KEYS.perangkat),
        getState<ProfilTersimpan>(SESI_STATE_KEYS.profil),
        bacaInstalSelesai(),
      ])
      const row = aktif === undefined || deviceId === undefined ? null : await bacaSesiLokal(aktif)
      if (row === null || deviceId === undefined) {
        useSesi.setState({ status: 'tanpa_sesi', instalSelesai })
        // No one is served here and no sign-in is held (a reload forgets one): a session still
        // stored belongs to no one on this device. A step a reload left (an admin's session the
        // password opened, the app's code never given) or a sign-in that never reached its PIN
        // is forgotten, not left in storage. A served person's device keeps another stored
        // session as ruled (addendum 9): the token check refuses it. A sign-in under way, and a
        // newcomer it left at Buat PIN, keep their own.
        //
        // Forgotten in this call and revoked by plain requests (`lupakanSesi`), never through
        // the SDK's sign-out: that one waits on its lock and the network and removes the stored
        // session only at its end, so a sign-in begun meanwhile would lose its new session (or
        // have it revoked), and would take the old one as the session to put back if it failed.
        // Launch and the next sign-in wait for no network.
        if (
          !adaMasuk() &&
          useSesi.getState().menunggu === null &&
          deps.auth.storedSession() !== null
        ) {
          deps.auth.lupakanSesi()
        }
        return
      }
      const sesi = sesiDariBaris(row, profil ?? null, deviceId)
      // Spec 15.4: data fetched for someone else (a device of an older build) is cleared for
      // the active user before anything shows; offline it waits for an online unlock.
      const periksa = await periksaLingkupAktif(db, sesi, deps.online())
      lingkupDitunda = periksa === 'ditunda'
      if (periksa === 'dibersihkan') await setState(SESI_STATE_KEYS.aktif, sesi.userId)
      // The identity is trusted before the PIN: the sync loop and the cached aggregates
      // run while the lock screen is up.
      await db.setScope(scopeOf(sesi))
      useSesi.setState({
        asli: sesi,
        status: 'terkunci',
        instalSelesai: instalSelesai && periksa !== 'dibersihkan',
        menunggu: null,
      })
      cacheNamaParoki(sesi)
      // Online, the server is asked for the role while the PIN is typed (never awaited: a
      // slow connection never holds the start up).
      void periksaPeran()
    },

    async masuk(email, kataSandi, lupaPin = false, opsi = {}) {
      // A new sign-in replaces one still waiting for its step: the served person's session
      // is back before anything else happens. An offer of a new app not taken goes too.
      tawaran = null
      await lepasTertunda()
      penggunaMasuk = null
      const gerbang: Gerbang = {
        sesiSebelum: deps.auth.storedSession(),
        dilayani: useSesi.getState().asli.userId,
      }
      return diGerbang(gerbang, async (): Promise<MasukHasil> => {
        if (!deps.online()) return { ok: false, alasan: 'offline' }
        const terkunci = useSesi.getState().asli.userId
        const tersimpan = lupaPin ? deps.auth.storedSession() : null
        memeriksaAkun = lupaPin
        let signIn: SignInResult
        try {
          signIn = await deps.auth.signIn(email, kataSandi)
          if (signIn.ok && lupaPin && signIn.userId !== terkunci) {
            await deps.auth.restoreSession(tersimpan)
            return { ok: false, alasan: 'akun_lain' }
          }
        } finally {
          memeriksaAkun = false
        }
        if (!signIn.ok) return signIn
        penggunaMasuk = signIn.userId
        const token = signIn.accessToken
        const adaFaktor = signIn.adaFaktor ?? null
        const buang = opsi.buangPerubahanLain === true
        const pemilik = await perubahanMilikLain(db, signIn.userId)
        if (pemilik.length > 0 && !buang) return { ok: false, alasan: 'perubahan_lain', pemilik }

        tertunda = {
          ...gerbang,
          pengguna: signIn.userId,
          email,
          kataSandi,
          lupaPin,
          buang,
          mulai: deps.now(),
          tiket: Object.freeze({ pengguna: signIn.userId, email, adaSandi: kataSandi !== '' }),
        }
        return teruskanMasuk(() => selesaikanMasuk(token, adaFaktor))
      })
    },

    emailTerakhir() {
      return emailAkun(useSesi.getState().asli.userId)
    },

    masukTertunda() {
      return tertundaHidup()?.tiket ?? null
    },

    batasMasuk(tiket) {
      const t = tertundaHidup()
      return t !== null && t.tiket === tiket ? t.mulai + IDLE_LOCK_MS : null
    },

    penggunaAkun,

    penggunaDilayani,

    async lanjutkanMasuk() {
      const t = tertunda
      if (t === null) return { ok: false, alasan: 'kedaluwarsa' }
      if (habis(t)) {
        await lepasTertunda()
        return { ok: false, alasan: 'kedaluwarsa' }
      }
      penggunaMasuk = t.pengguna
      return diGerbang(t, () =>
        teruskanMasuk(async (): Promise<MasukHasil> => {
          if (!deps.online()) return { ok: false, alasan: 'offline' }
          // The waiting account's session, as the step left it (aal2 after the code).
          const token = await deps.auth.accessToken()
          if (token === null) return { ok: false, alasan: 'kedaluwarsa' }
          return selesaikanMasuk(token)
        }),
      )
    },

    async batalkanMasuk() {
      const t = tertunda
      if (t === null) return
      if (t.pengguna !== t.dilayani) {
        await lepasTertunda()
        return
      }
      // The device served this very person: no one else's session to put back, and the
      // half-done one does not stay.
      try {
        await deps.auth.signOutLocal()
      } finally {
        tertunda = null
      }
    },

    async gantiSandi(untuk, baru, lama) {
      // GoTrue keeps at most 72 bytes (bcrypt): a longer password never leaves the device.
      if (lebihDariMaks(baru)) return { ok: false, kode: 'invalid_request' }
      const akun = await tokenAkun(untuk)
      if (akun === null) return { ok: false, kode: deps.online() ? 'kedaluwarsa' : 'offline' }
      const { token, t, pengguna } = akun
      const sekarang = lama ?? t?.kataSandi ?? ''
      // Mid-session the sign-in again (below) uses the account's e-mail as GoTrue has it now,
      // read while this session still stands (the change may end it): one changed elsewhere is
      // newer than the profile's. A sign-in's own e-mail was just typed.
      const emailKini = t === null ? ((await deps.auth.email?.()) ?? null) : null
      const body: Record<string, string> = { kata_sandi_baru: baru }
      if (sekarang !== '') body.kata_sandi_lama = sekarang
      const r = await api(token, null, 'POST', '/akun/kata-sandi', body)
      if (r.kind !== 'ok') return hasilGagal(r)
      if (t !== null) t.kataSandi = baru
      // The new token no longer carries harus_ganti_sandi. GoTrue is expected to end every
      // session of the account with the new password: the device then signs in again with it
      // (a waiting sign-in stays held, waiting; mid-session the same person carries on).
      if ((await deps.auth.segarkan()) !== null) return { ok: true }
      const email = t?.email ?? emailKini ?? (await emailAkun(pengguna))
      if (email === null) return { ok: true }
      const lagi = await deps.auth.signIn(email, baru)
      if (lagi.ok && lagi.userId !== pengguna) {
        // Never keep another account's session (an e-mail that moved to someone else).
        await deps.auth.signOutLocal()
      } else if (lagi.ok && t === null) {
        useSesi.setState({ perluMasukKembali: false })
      }
      return { ok: true }
    },

    async kirimKodePemulihan(untuk) {
      const akun = await tokenAkun(untuk)
      if (akun === null) return { ok: false, kode: deps.online() ? 'kedaluwarsa' : 'offline' }
      const { token } = akun
      const r = await api(token, null, 'POST', '/akun/pemulihan/kirim', {})
      if (
        r.kind === 'ok' &&
        isRecord(r.body) &&
        typeof r.body.email === 'string' &&
        typeof r.body.berlaku_sampai === 'string'
      ) {
        return { ok: true, email: r.body.email, berlakuSampai: r.body.berlaku_sampai }
      }
      return hasilGagal(r)
    },

    async periksaKodePemulihan(untuk, kode) {
      const akun = await tokenAkun(untuk)
      if (akun === null) return { ok: false, kode: deps.online() ? 'kedaluwarsa' : 'offline' }
      const { token, t } = akun
      const r = await api(token, null, 'POST', '/akun/pemulihan/periksa', { kode })
      if (r.kind !== 'ok') return hasilGagal(r)
      // The factors are gone and GoTrue ended every session of the account.
      if (t === null) {
        // Mid-session no password was typed here: the person signs in again on Masuk, which
        // locks the session it finds open (the PIN shows why if it is opened instead).
        await deps.auth.signOutLocal()
        if (useSesi.getState().asli.userId === akun.pengguna) {
          useSesi.setState({ perluMasukKembali: true })
        }
        return { ok: true, masukLagi: null }
      }
      // During a sign-in the password typed signs in again, through the same gate; a second
      // sign-in that fails drops the waiting one (the served person's session comes back).
      penggunaMasuk = t.pengguna
      let masukLagi: MasukHasil | null
      try {
        masukLagi = await diGerbang(t, () =>
          teruskanMasuk(async (): Promise<MasukHasil | null> => {
            await deps.auth.signOutLocal()
            if (t.kataSandi === '') return null
            const signIn = await deps.auth.signIn(t.email, t.kataSandi)
            if (!signIn.ok) return null
            return selesaikanMasuk(signIn.accessToken, signIn.adaFaktor ?? null)
          }),
        )
      } catch {
        // The code was right and the factors are gone; only the sign-in again failed (the
        // worker refused to ready the device, a storage failure). The gate dropped it as any
        // failed second sign-in: the person signs in again on Masuk for the new app.
        masukLagi = null
      }
      // A role that may go without the app went through: the new app is offered, once.
      if (masukLagi?.ok && !menungguLangkah(masukLagi)) tawaran = t.pengguna
      return { ok: true, masukLagi }
    },

    async catatDuaLangkah(untuk) {
      const akun = await tokenAkun(untuk)
      if (akun === null) return
      const { token } = akun
      const r = await api(token, null, 'POST', '/akun/dua-langkah', {})
      // Only a note to the server; a wipe it answers is never hidden.
      if (r.kind === 'wipe') await hasilGagal(r)
    },

    tawaranPasang() {
      const { status, menunggu } = useSesi.getState()
      return (
        tawaran !== null && tawaran === penggunaAkun() && (status === 'aktif' || menunggu !== null)
      )
    },

    lepasTawaranPasang() {
      tawaran = null
    },

    async sesiBerakhir(untuk) {
      if (typeof untuk !== 'string') {
        // Only the sign-in this ticket names, if it still waits.
        if (tertunda !== null && tertunda.tiket === untuk) await controller.batalkanMasuk()
        return
      }
      const { asli: sesi, status } = useSesi.getState()
      if (untuk !== '' && untuk === sesi.userId && status !== 'tanpa_sesi') {
        useSesi.setState({ perluMasukKembali: true })
      }
    },

    selesaikanLangkahAkun() {
      useSesi.setState({ langkahAkun: null })
      void db.syncNow().catch(() => {})
    },

    periksaPeran,

    async perbaruiNama(nama) {
      const { asli: sesi } = useSesi.getState()
      if (sesi.userId === '') return
      await run('UPDATE sesi_lokal SET nama = ? WHERE pengguna_id = ?', [nama, sesi.userId])
      // The parish worked in stays the store's: the worker hears nothing of a new name, so this
      // device's stored choice (another tab's, or an older one) is not read again here.
      const kini = useSesi.getState()
      if (kini.asli.userId !== sesi.userId) return
      useSesi.setState({ asli: { ...kini.asli, nama }, bekerjaDi: kini.bekerjaDi })
    },

    async perbaruiEmail(pengguna, email) {
      // Read from another account's stored session (a sign-in waiting, another tab's): not hers.
      if (pengguna === '' || pengguna !== penggunaDilayani()) return
      const profil = await getState<ProfilTersimpan>(SESI_STATE_KEYS.profil)
      if (profil === undefined || profil === null || profil.pengguna_id !== pengguna) return
      if (profil.email === email) return
      // Nothing began waiting, nor did the device change hands, while the profile was read.
      if (pengguna !== penggunaDilayani()) return
      await setState(SESI_STATE_KEYS.profil, { ...profil, email })
    },

    async simpanPin(pin) {
      const sesi = useSesi.getState().menunggu
      if (sesi === null) throw new Error('simpanPin tanpa login yang menunggu')
      const garam = makeSalt()
      const hash = await hashPin(pin, garam)
      const stamp = new Date(deps.now()).toISOString()
      await run(
        'INSERT INTO sesi_lokal (pengguna_id, nama, peran, paroki_id, lingkungan_id, stasi_id, pin_hash, pin_garam, pin_gagal, pin_terkunci_sampai, terakhir_diverifikasi, terakhir_aktif) VALUES (?, ?, ?, ?, ?, ?, ?, ?, 0, NULL, ?, ?) ON CONFLICT(pengguna_id) DO UPDATE SET nama = excluded.nama, peran = excluded.peran, paroki_id = excluded.paroki_id, lingkungan_id = excluded.lingkungan_id, stasi_id = excluded.stasi_id, pin_hash = excluded.pin_hash, pin_garam = excluded.pin_garam, pin_gagal = 0, pin_terkunci_sampai = NULL, terakhir_diverifikasi = excluded.terakhir_diverifikasi, terakhir_aktif = excluded.terakhir_aktif',
        [
          sesi.userId,
          sesi.nama,
          sesi.peran,
          sesi.parokiId,
          sesi.lingkunganId,
          sesi.stasiId,
          hash,
          garam,
          stamp,
          stamp,
        ],
      )
      await setState(SESI_STATE_KEYS.aktif, sesi.userId)
      const instalSelesai = await bacaInstalSelesai()
      aktifkan(sesi, instalSelesai)
      return instalSelesai ? 'beranda' : 'unduhan'
    },

    async buka(pin) {
      const { asli: sesi } = useSesi.getState()
      const row = await bacaSesiLokal(sesi.userId)
      if (row === null) {
        useSesi.setState({ status: 'tanpa_sesi', asli: SESI_KOSONG })
        return { ok: false, alasan: 'salah' }
      }
      const state: PinLockState = { gagal: row.pin_gagal, terkunciSampai: row.pin_terkunci_sampai }
      if (pinLockedUntil(state, deps.now()) !== null) return { ok: false, alasan: 'terkunci' }
      const correct = await verifyPin(pin, row.pin_garam, row.pin_hash)
      const outcome = nextPinState(state, correct, deps.now())
      await tulisKunciPin(row.pengguna_id, outcome.next)
      if (!outcome.ok) return { ok: false, alasan: outcome.alasan }
      // The device's own person is back: a sign-in left waiting for a step on this device
      // is dropped, and the session it replaced comes back with the sync (spec 15.4).
      await lepasTertunda()
      if (lingkupDitunda && deps.online()) {
        // No role check runs while this one is pending (`bolehPeriksaPeran`); should one still
        // be in flight, it ends first and this check reads the session it left.
        if (pemeriksaanPeran !== null) await pemeriksaanPeran
        const kini = useSesi.getState().asli
        const periksa = await periksaLingkupAktif(db, kini, true)
        lingkupDitunda = periksa === 'ditunda'
        if (periksa === 'dibersihkan') {
          await setState(SESI_STATE_KEYS.aktif, kini.userId)
          await db.setScope(scopeOf(kini))
          useSesi.setState({ instalSelesai: false })
        }
      }
      useSesi.setState({ status: 'aktif' })
      // Online, the server is asked for the role again (not awaited: the unlock is never held up).
      void periksaPeran()
      return { ok: true }
    },

    async kunci() {
      const { asli: sesi, status } = useSesi.getState()
      if (status !== 'aktif') return
      // Whoever opens it again has given the PIN, not the recovery: no offer survives the lock.
      tawaran = null
      useSesi.setState({ status: 'terkunci' })
      await run('UPDATE sesi_lokal SET terakhir_aktif = ? WHERE pengguna_id = ?', [
        new Date(deps.now()).toISOString(),
        sesi.userId,
      ])
    },

    async ubahPin(pinLama, pinBaru) {
      const { asli: sesi } = useSesi.getState()
      const row = await bacaSesiLokal(sesi.userId)
      if (row === null) return { ok: false, alasan: 'salah' }
      const state: PinLockState = { gagal: row.pin_gagal, terkunciSampai: row.pin_terkunci_sampai }
      if (pinLockedUntil(state, deps.now()) !== null) return { ok: false, alasan: 'terkunci' }
      const correct = await verifyPin(pinLama, row.pin_garam, row.pin_hash)
      const outcome = nextPinState(state, correct, deps.now())
      if (!outcome.ok) {
        await tulisKunciPin(row.pengguna_id, outcome.next)
        return { ok: false, alasan: outcome.alasan }
      }
      const garam = makeSalt()
      const hash = await hashPin(pinBaru, garam)
      await run(
        'UPDATE sesi_lokal SET pin_hash = ?, pin_garam = ?, pin_gagal = 0, pin_terkunci_sampai = NULL WHERE pengguna_id = ?',
        [hash, garam, row.pengguna_id],
      )
      return { ok: true }
    },

    kirimSebelumKeluar(batasMs) {
      return kirimSebelumKeluar(db, useSesi.getState().asli.userId, batasMs)
    },

    async keluar() {
      const { asli: sesi } = useSesi.getState()
      await db.setScope(null)
      // A sign-in left waiting goes with the sign-out (no scope remains for its token), and
      // so does an offer of a new app not taken.
      tertunda = null
      tawaran = null
      if (sesi.userId.length > 0) {
        await run('DELETE FROM sesi_lokal WHERE pengguna_id = ?', [sesi.userId])
      }
      await setState(SESI_STATE_KEYS.aktif, null)
      await deps.auth.signOutLocal()
      useSesi.setState({
        asli: SESI_KOSONG,
        status: 'tanpa_sesi',
        menunggu: null,
        perluMasukKembali: false,
        langkahAkun: null,
        sandiDiganti: false,
        peranBerubah: false,
      })
    },

    async perbaruiNamaPendek(namaPendek) {
      const { asli: sesi } = useSesi.getState()
      if (sesi.paroki === null) return
      const profil = await getState<ProfilTersimpan>(SESI_STATE_KEYS.profil)
      if (profil !== undefined && profil !== null && profil.pengguna_id === sesi.userId) {
        await setState(SESI_STATE_KEYS.profil, { ...profil, nama_pendek: namaPendek })
      }
      const next: Sesi = {
        ...sesi,
        paroki: { nama: sesi.paroki.nama, pendek: namaPendek ?? sesi.paroki.nama },
      }
      useSesi.setState({ asli: next })
      cacheNamaParoki(next)
    },

    async selesaikanInstal() {
      await setState(SESI_STATE_KEYS.instalSelesai, true)
      useSesi.setState({ instalSelesai: true, peranBerubah: false })
    },

    async konfirmasiPenghapusan() {
      try {
        await db.setScope(null)
      } catch {
        // The worker may already be gone; the wipe does not depend on it.
      }
      // The wipe takes every stored session with it, a waiting sign-in's too.
      tertunda = null
      await deps.wipe()
      useSesi.setState({
        asli: SESI_KOSONG,
        status: 'tanpa_sesi',
        menunggu: null,
        penghapusan: null,
        perluMasukKembali: false,
        instalSelesai: false,
        langkahAkun: null,
        sandiDiganti: false,
        peranBerubah: false,
      })
    },

    async pinTerkunciSampai() {
      const { asli: sesi } = useSesi.getState()
      const row = await bacaSesiLokal(sesi.userId)
      if (row === null) return null
      return pinLockedUntil(
        { gagal: row.pin_gagal, terkunciSampai: row.pin_terkunci_sampai },
        deps.now(),
      )
    },

    async sudahAdaKursor() {
      return (await getState<number>(SESI_STATE_KEYS.kursor)) !== undefined
    },
  }

  return controller
}
